import { useCallback, useEffect, useMemo, useState } from 'react';
import { getEnabledOperationalCatalog, OPERATIONAL_PLACEMENTS } from '../../../config/operationalTileCatalog';
import { customExecutiveMetricsService } from '../../../services/customExecutiveMetricsService';

function formatHighlightValue(value, unit) {
  if (value == null || value === '') return '—';
  const num = Number(value);
  if (Number.isNaN(num)) return String(value);
  if (unit === 'percent') return `${num}%`;
  if (unit === 'currency') return `$${num.toLocaleString()}`;
  if (unit === 'days') return `${num}d`;
  return num.toLocaleString();
}

function toHighlightTile(tile) {
  const historyValues = (tile.history || []).map((h) => h.value).filter((v) => v != null);
  const delta =
    tile.value != null && tile.compareValue != null
      ? Number((tile.value - tile.compareValue).toFixed(1))
      : tile.value != null && historyValues.length >= 2
        ? Number((tile.value - historyValues[historyValues.length - 2]).toFixed(1))
        : null;
  return {
    id: tile.id,
    label: tile.label,
    value: tile.value,
    unit: tile.unit,
    delta,
    polarity: tile.polarity || 'higher_is_better',
    sourceLabel: tile.sourceLabel,
    displayValue: formatHighlightValue(tile.value, tile.unit),
    custom: true,
    sparkline: normalizeHighlightSparkline(tile.sparkline?.length ? tile.sparkline : historyValues),
  };
}

function normalizeHighlightSparkline(values) {
  if (!values?.length) return [];
  const nums = values.map(Number).filter((n) => !Number.isNaN(n));
  if (!nums.length) return [];
  const min = Math.min(...nums);
  const max = Math.max(...nums);
  const span = max - min || 1;
  return nums.map((n) => Math.round(20 + ((n - min) / span) * 80));
}

export function useOperationalLayout(organizationId) {
  const [layoutConfig, setLayoutConfig] = useState({ layout: {}, tiles: [] });
  const [loading, setLoading] = useState(Boolean(organizationId));

  const loadConfig = useCallback(async () => {
    if (!organizationId) {
      setLayoutConfig({ layout: {}, tiles: [] });
      setLoading(false);
      return;
    }
    setLoading(true);
    try {
      const config = await customExecutiveMetricsService.getExecutiveConfig(String(organizationId));
      setLayoutConfig({
        layout: config.layout || {},
        tiles: config.tiles || [],
      });
    } finally {
      setLoading(false);
    }
  }, [organizationId]);

  useEffect(() => {
    loadConfig();
  }, [loadConfig]);

  useEffect(() => {
    const onUpdate = () => loadConfig();
    window.addEventListener('executive-custom-metrics-updated', onUpdate);
    return () => window.removeEventListener('executive-custom-metrics-updated', onUpdate);
  }, [loadConfig]);

  const enabledSections = useMemo(
    () => getEnabledOperationalCatalog(layoutConfig.layout).sections,
    [layoutConfig.layout]
  );

  const enabledSectionIds = useMemo(
    () => enabledSections.map((section) => section.id),
    [enabledSections]
  );

  const highlightTiles = useMemo(
    () =>
      (layoutConfig.tiles || [])
        .filter((tile) => tile.placement === OPERATIONAL_PLACEMENTS.HIGHLIGHT)
        .map(toHighlightTile),
    [layoutConfig.tiles]
  );

  return {
    layout: layoutConfig.layout,
    enabledSections,
    enabledSectionIds,
    highlightTiles,
    loading,
    reload: loadConfig,
  };
}
